import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { dataLocalParaInput } from '../../core/date-utils';
import { Cliente, ContagemAiItemSugestao, ContagemAnexo, ContagemFotoUpload, NotaTransferencia, Produto, TAMANHOS, Usuario } from '../../core/models';

type ContagemFormItem = { id_produto: number; tamanho: string; quantidade: number; defeitos: number };

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf],
  template: `
    <header class="page-header">
      <div>
        <h2>{{ id ? 'Editar contagem' : 'Nova contagem' }}</h2>
        <p>Informe a quantidade final por produto e tamanho</p>
      </div>
    </header>

    <form class="panel" [formGroup]="form" (ngSubmit)="salvar(false)">
      <div class="grid-form">
        <input type="hidden" formControlName="id_nota">
        <label>Cliente
          <select formControlName="id_cliente">
            <option value="">Selecione</option>
            <option *ngFor="let cliente of clientes" [value]="cliente.id_cliente">{{ cliente.nome }}</option>
          </select>
        </label>
        <label>Data da contagem<input type="date" formControlName="data_contagem"></label>
        <label>Referência<input formControlName="referencia"></label>
        <label>Descrição<input formControlName="descricao"></label>
        <label>Cor<input formControlName="cor"></label>
        <div class="responsaveis-box">
          <label>Responsável
            <select formControlName="responsavel_para_adicionar">
              <option value="">Selecione</option>
              <option *ngFor="let usuario of usuariosDisponiveis" [value]="usuario.id_usuario">{{ usuario.nome }}</option>
            </select>
          </label>
          <button type="button" (click)="adicionarResponsavel()">Adicionar</button>
          <div class="selected-list" *ngIf="responsaveisSelecionados.length">
            <span class="chip" *ngFor="let usuario of responsaveisSelecionados">
              {{ usuario.nome }}
              <button type="button" class="chip-remove" (click)="removerResponsavel(usuario.id_usuario)">Remover</button>
            </span>
          </div>
          <p class="field-hint" *ngIf="!responsaveisSelecionados.length">Nenhum responsável adicionado</p>
        </div>
      </div>

      <label class="full">Observação<textarea rows="3" formControlName="observacao"></textarea></label>

      <div class="draft-photo">
        <div>
          <span class="field-label">Foto do rascunho</span>
          <p class="field-hint">Tire uma foto agora ou anexe uma imagem ja salva.</p>
        </div>
        <input #cameraInput class="sr-only" type="file" accept="image/*" capture="environment" (change)="selecionarRascunho($event)">
        <input #arquivoInput class="sr-only" type="file" accept="image/*" (change)="selecionarRascunho($event)">
        <div class="draft-photo-actions">
          <button type="button" (click)="cameraInput.click()">Tirar foto</button>
          <button class="ghost" type="button" (click)="arquivoInput.click()">Anexar foto</button>
          <button class="ghost" type="button" (click)="preencherComIa()" [disabled]="!iaHabilitada || !rascunhoFoto || preenchendoComIa">
            {{ preenchendoComIa ? 'Lendo foto...' : 'Preencher com IA' }}
          </button>
          <button class="ghost danger-action" type="button" *ngIf="rascunhoPreviewUrl" (click)="removerRascunho()">Remover</button>
        </div>
        <p class="field-hint" *ngIf="iaMensagem">{{ iaMensagem }}</p>
        <figure class="draft-photo-preview" *ngIf="rascunhoPreviewUrl">
          <img [src]="rascunhoPreviewUrl" alt="Foto do rascunho da contagem">
          <figcaption>{{ rascunhoArquivoNome }}</figcaption>
        </figure>
      </div>

      <div class="inline-picker">
        <label>Produto
          <select formControlName="produto_para_adicionar" (change)="selecionarProdutoParaAdicionar()">
            <option value="">Selecione</option>
            <option *ngFor="let produto of produtosDisponiveis" [value]="produto.cod_produto">{{ produto.codigo_produto }} - {{ produto.descricao }}</option>
          </select>
        </label>
        <label>Tamanho
          <select formControlName="tamanho_para_adicionar">
            <option value="">Selecione</option>
            <option *ngFor="let tamanho of tamanhosDisponiveis" [value]="tamanho">{{ tamanho }}</option>
          </select>
        </label>
        <button class="ghost" type="button" (click)="adicionarItem()" [disabled]="!form.controls.produto_para_adicionar.value || !form.controls.tamanho_para_adicionar.value">Adicionar item</button>
      </div>

      <table class="size-table" formArrayName="itens" *ngIf="itens.length">
        <thead><tr><th>Produto</th><th>Tamanho</th><th>Quantidade</th><th>Defeitos</th><th>Total</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let item of itens.controls; let i = index" [formGroupName]="i">
            <td data-label="Produto">{{ produtoLabel(item.value.id_produto) }}</td>
            <td data-label="Tamanho">{{ item.value.tamanho }}</td>
            <td data-label="Quantidade"><input type="number" min="0" formControlName="quantidade"></td>
            <td data-label="Defeitos"><input type="number" min="0" formControlName="defeitos"></td>
            <td data-label="Total">{{ totalItem(item.value) }}</td>
            <td class="actions" data-label="Ações"><button class="ghost" type="button" (click)="removerItem(i)">Remover</button></td>
          </tr>
        </tbody>
      </table>
      <p class="field-hint" *ngIf="!itens.length">Nenhum item selecionado</p>

      <div class="total-line">Total geral: <strong>{{ total }}</strong> peças</div>
      <p class="error" *ngIf="erro">{{ erro }}</p>

      <div class="form-actions">
        <button type="submit" [disabled]="form.invalid">Salvar contagem</button>
        <button type="button" (click)="salvar(true)" [disabled]="form.invalid">Finalizar contagem</button>
        <button class="ghost" type="button" *ngIf="id" (click)="romaneio()">Gerar romaneio</button>
      </div>
    </form>
  `
})
export class ContagemFormComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  clientes: Cliente[] = [];
  notas: NotaTransferencia[] = [];
  produtos: Produto[] = [];
  produtosDaNota: number[] = [];
  tamanhosConferidosPorProduto = new Map<number, string[]>();
  usuarios: Usuario[] = [];
  id = Number(this.route.snapshot.paramMap.get('id') || 0);
  erro = '';
  rascunhoFoto?: ContagemFotoUpload;
  rascunhoExistente?: ContagemAnexo;
  rascunhoPreviewUrl = '';
  rascunhoArquivoNome = '';
  removerRascunhoExistente = false;
  preenchendoComIa = false;
  iaHabilitada = false;
  iaMensagem = '';
  private rascunhoObjectUrl = '';

  form = this.fb.nonNullable.group({
    id_contagem: [0],
    id_nota: [''],
    id_cliente: ['', Validators.required],
    id_produto: [''],
    data_contagem: [dataLocalParaInput(), Validators.required],
    referencia: [''],
    descricao: [''],
    cor: [''],
    id_usuarios: this.fb.nonNullable.control<string[]>([], Validators.required),
    responsavel_para_adicionar: [''],
    produto_para_adicionar: [''],
    tamanho_para_adicionar: [''],
    observacao: [''],
    itens: this.fb.array([])
  });

  get itens() {
    return this.form.controls.itens as FormArray;
  }

  get total() {
    return this.itens.controls.reduce((sum, control) => sum + this.totalItem(control.value), 0);
  }

  get responsaveisSelecionados() {
    const ids = this.form.controls.id_usuarios.value.map(Number);
    return this.usuarios.filter((usuario) => ids.includes(usuario.id_usuario));
  }

  get usuariosDisponiveis() {
    const ids = this.form.controls.id_usuarios.value.map(Number);
    return this.usuarios.filter((usuario) => !ids.includes(usuario.id_usuario));
  }

  get produtosDisponiveis() {
    if (this.form.controls.id_nota.value) {
      return this.produtos.filter((produto) => this.produtosDaNota.includes(produto.cod_produto));
    }
    return this.produtos;
  }

  get tamanhosDisponiveis() {
    const idProduto = Number(this.form.controls.produto_para_adicionar.value || 0);
    const selecionados = this.itens.controls
      .filter((control) => Number(control.value.id_produto || 0) === idProduto)
      .map((control) => control.value.tamanho);
    return this.tamanhosBaseProduto(idProduto).filter((tamanho) => !selecionados.includes(tamanho));
  }

  ngOnInit() {
    this.api.contagemIaConfig().subscribe({
      next: (config) => (this.iaHabilitada = !!config.enabled),
      error: () => (this.iaHabilitada = false)
    });
    this.api.clientes().subscribe((clientes) => (this.clientes = clientes.filter((c) => c.ativo)));
    this.api.produtos().subscribe((produtos) => (this.produtos = produtos.filter((p) => p.ativo)));
    this.api.usuarios().subscribe((usuarios) => (this.usuarios = usuarios.filter((u) => u.ativo)));
    this.api.notas().subscribe((notas) => {
      this.notas = notas.filter((nota) => !['CONFERIDA', 'DIVERGENTE', 'FINALIZADA', 'CANCELADA'].includes(nota.status));
      const idNota = this.route.snapshot.queryParamMap.get('nota');
      if (!this.id && idNota) {
        this.form.patchValue({ id_nota: idNota });
        this.selecionarNota();
      }
    });
    if (this.id) {
      this.api.contagem(this.id).subscribe((contagem) => {
        if (contagem.status !== 'ABERTA') this.router.navigateByUrl(`/contagens/${contagem.id_contagem}`);
        this.form.patchValue({
          id_contagem: contagem.id_contagem,
          id_nota: contagem.id_nota ? String(contagem.id_nota) : '',
          id_cliente: String(contagem.id_cliente),
          id_produto: contagem.id_produto ? String(contagem.id_produto) : '',
          data_contagem: String(contagem.data_contagem).slice(0, 10),
          referencia: contagem.referencia || '',
          descricao: contagem.descricao || '',
          cor: contagem.cor || '',
          id_usuarios: this.idsUsuarios(contagem),
          responsavel_para_adicionar: '',
          produto_para_adicionar: '',
          tamanho_para_adicionar: '',
          observacao: contagem.observacao || ''
        });
        this.aplicarRascunhoExistente(contagem.anexos || []);
        this.itens.clear();
        contagem.itens
          .filter((item) => Number(item.quantidade || 0) + Number(item.defeitos || 0) > 0)
          .forEach((item) => this.itens.push(this.itemGroup(
            Number(item.id_produto || contagem.id_produto || 0),
            item.tamanho,
            item.quantidade,
            Number(item.defeitos || 0)
          )));
        if (contagem.id_nota) {
          this.api.nota(contagem.id_nota).subscribe((nota) => this.aplicarRestricoesDaNota(nota));
        }
      });
    }
  }

  ngOnDestroy() {
    this.liberarPreviewTemporario();
  }

  salvar(finalizar: boolean) {
    this.erro = '';
    this.form.markAllAsTouched();
    if (this.form.invalid || this.total <= 0) {
      this.erro = 'Informe cliente, responsáveis, produtos, tamanhos e quantidades';
      return;
    }

    const raw = this.form.getRawValue();
    const itens = raw.itens as ContagemFormItem[];
    const payload = {
      ...raw,
      id_contagem: raw.id_contagem || undefined,
      id_nota: raw.id_nota ? Number(raw.id_nota) : undefined,
      id_cliente: Number(raw.id_cliente),
      id_produto: itens[0]?.id_produto,
      id_usuarios: raw.id_usuarios.map((id) => Number(id)),
      responsavel_para_adicionar: undefined,
      produto_para_adicionar: undefined,
      tamanho_para_adicionar: undefined,
      rascunho_foto: this.rascunhoFoto || undefined,
      remover_rascunho: this.removerRascunhoExistente && !this.rascunhoFoto,
      itens: itens
        .map((item) => ({
          id_produto: Number(item.id_produto || 0),
          tamanho: item.tamanho,
          quantidade: Number(item.quantidade || 0),
          defeitos: Number(item.defeitos || 0)
        }))
        .filter((item) => item.quantidade + item.defeitos > 0)
    };

    this.api.salvarContagem(payload).subscribe({
      next: (contagem) => {
        if (finalizar) {
          this.api.finalizarContagem(contagem.id_contagem).subscribe(() => this.router.navigateByUrl('/contagens'));
        } else {
          this.router.navigateByUrl(`/contagens/${contagem.id_contagem}`);
        }
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao salvar contagem')
    });
  }

  async selecionarRascunho(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.erro = '';
    try {
      const foto = await this.prepararImagemRascunho(file);
      this.rascunhoFoto = foto;
      this.rascunhoArquivoNome = foto.nome;
      this.iaMensagem = '';
      this.removerRascunhoExistente = !!this.rascunhoExistente;
      this.definirPreviewRascunho(foto.dados);
    } catch (error) {
      this.erro = error instanceof Error ? error.message : 'Erro ao preparar foto do rascunho';
    }
  }

  removerRascunho() {
    this.rascunhoFoto = undefined;
    this.rascunhoArquivoNome = '';
    this.iaMensagem = '';
    this.removerRascunhoExistente = !!this.rascunhoExistente;
    this.definirPreviewRascunho('');
  }

  preencherComIa() {
    if (!this.iaHabilitada || !this.rascunhoFoto || this.preenchendoComIa) return;

    const produtos = this.produtosPermitidosParaIa();
    if (!produtos.length) {
      this.erro = 'Selecione uma nota ou cadastre produtos ativos antes de usar IA';
      return;
    }

    this.erro = '';
    this.iaMensagem = '';
    this.preenchendoComIa = true;
    this.api.extrairContagemFoto({
      imagem: this.rascunhoFoto.dados,
      produtos,
      usuarios: this.usuariosPermitidosParaIa()
    }).subscribe({
      next: (sugestao) => {
        const totalItens = this.aplicarSugestoesIa(sugestao.itens || []);
        const totalResponsaveis = this.aplicarResponsaveisIa(sugestao.responsaveis || []);
        this.preenchendoComIa = false;
        this.iaMensagem = totalItens || totalResponsaveis
          ? `IA preencheu ${totalItens} item(ns) e ${totalResponsaveis} responsavel(is). Confira os dados antes de salvar.`
          : 'A IA nao encontrou itens compativeis com os produtos e tamanhos disponiveis.';
      },
      error: (error) => {
        this.preenchendoComIa = false;
        this.erro = error.error?.message || 'Erro ao preencher com IA';
      }
    });
  }

  adicionarResponsavel() {
    const idUsuario = this.form.controls.responsavel_para_adicionar.value;
    if (!idUsuario) return;

    const atuais = this.form.controls.id_usuarios.value;
    if (!atuais.includes(idUsuario)) {
      this.form.controls.id_usuarios.setValue([...atuais, idUsuario]);
      this.form.controls.id_usuarios.markAsDirty();
      this.form.controls.id_usuarios.updateValueAndValidity();
    }

    this.form.controls.responsavel_para_adicionar.setValue('');
  }

  removerResponsavel(idUsuario: number) {
    const next = this.form.controls.id_usuarios.value.filter((id) => Number(id) !== idUsuario);
    this.form.controls.id_usuarios.setValue(next);
    this.form.controls.id_usuarios.markAsDirty();
    this.form.controls.id_usuarios.updateValueAndValidity();
  }

  adicionarItem() {
    const idProduto = Number(this.form.controls.produto_para_adicionar.value || 0);
    const tamanho = this.form.controls.tamanho_para_adicionar.value;
    if (!idProduto || !tamanho) return;
    if (this.itens.controls.some((item) => Number(item.value.id_produto || 0) === idProduto && item.value.tamanho === tamanho)) return;

    this.itens.push(this.itemGroup(idProduto, tamanho, 1, 0));
    this.form.controls.id_produto.setValue(String(this.itens.at(0)?.value.id_produto || idProduto));
    const produto = this.produtos.find((item) => item.cod_produto === idProduto);
    if (produto && !this.form.controls.referencia.value) this.form.controls.referencia.setValue(produto.codigo_produto);
    if (produto && !this.form.controls.descricao.value) this.form.controls.descricao.setValue(produto.descricao);
    this.form.controls.tamanho_para_adicionar.setValue('');
  }

  removerItem(index: number) {
    this.itens.removeAt(index);
    this.form.controls.id_produto.setValue(String(this.itens.at(0)?.value.id_produto || ''));
  }

  romaneio() {
    this.api.abrirRomaneio(this.id);
  }

  selecionarNota() {
    const idNota = Number(this.form.controls.id_nota.value || 0);
    this.produtosDaNota = [];
    this.tamanhosConferidosPorProduto.clear();
    this.form.controls.produto_para_adicionar.setValue('');
    this.form.controls.tamanho_para_adicionar.setValue('');
    this.itens.clear();
    if (!idNota) return;

    this.api.nota(idNota).subscribe((nota) => {
      this.aplicarRestricoesDaNota(nota);
      const primeiroProduto = this.produtosDaNota[0];
      this.form.patchValue({
        id_cliente: String(nota.id_cliente),
        id_produto: primeiroProduto ? String(primeiroProduto) : '',
        produto_para_adicionar: this.produtosDaNota.length === 1 ? String(primeiroProduto) : '',
        referencia: nota.numero_nota,
        descricao: nota.produtos_resumo || nota.produto,
        cor: nota.cor || ''
      });
    });
  }

  selecionarProdutoParaAdicionar() {
    const tamanhoAtual = this.form.controls.tamanho_para_adicionar.value;
    if (tamanhoAtual && !this.tamanhosDisponiveis.includes(tamanhoAtual)) {
      this.form.controls.tamanho_para_adicionar.setValue('');
    }
  }

  produtoLabel(idProduto: number) {
    const produto = this.produtos.find((item) => item.cod_produto === Number(idProduto || 0));
    return produto ? `${produto.codigo_produto} - ${produto.descricao}` : idProduto;
  }

  totalItem(item: { quantidade?: number | string | null; defeitos?: number | string | null }) {
    return Number(item.quantidade || 0) + Number(item.defeitos || 0);
  }

  private idsUsuarios(contagem: { usuarios?: Usuario[]; id_usuarios?: number[]; id_usuario?: number }) {
    if (contagem.id_usuarios?.length) return contagem.id_usuarios.map(String);
    if (contagem.usuarios?.length) return contagem.usuarios.map((usuario) => String(usuario.id_usuario));
    return contagem.id_usuario ? [String(contagem.id_usuario)] : [];
  }

  private tamanhosBaseProduto(idProduto: number) {
    if (!idProduto) return [];
    if (!this.form.controls.id_nota.value) return TAMANHOS;
    return this.tamanhosConferidosPorProduto.get(idProduto) || [];
  }

  private produtosPermitidosParaIa() {
    return this.produtosDisponiveis
      .map((produto) => ({
        id_produto: produto.cod_produto,
        codigo_produto: produto.codigo_produto,
        produto: produto.descricao,
        tamanhos: this.tamanhosBaseProduto(produto.cod_produto)
      }))
      .filter((produto) => produto.tamanhos.length);
  }

  private usuariosPermitidosParaIa() {
    return this.usuarios.map((usuario) => ({
      id_usuario: usuario.id_usuario,
      nome: usuario.nome
    }));
  }

  private aplicarResponsaveisIa(responsaveis: { id_usuario: number }[]) {
    const idsAtuais = this.form.controls.id_usuarios.value;
    const idsPermitidos = new Set(this.usuarios.map((usuario) => Number(usuario.id_usuario || 0)));
    const proximos = [...idsAtuais];
    let aplicados = 0;

    for (const responsavel of responsaveis || []) {
      const idUsuario = Number(responsavel.id_usuario || 0);
      const idTexto = String(idUsuario);
      if (!idsPermitidos.has(idUsuario) || proximos.includes(idTexto)) continue;
      proximos.push(idTexto);
      aplicados += 1;
    }

    if (aplicados) {
      this.form.controls.id_usuarios.setValue(proximos);
      this.form.controls.id_usuarios.markAsDirty();
      this.form.controls.id_usuarios.updateValueAndValidity();
    }

    return aplicados;
  }

  private aplicarSugestoesIa(itens: ContagemAiItemSugestao[]) {
    let aplicados = 0;
    for (const item of itens) {
      const idProduto = Number(item.id_produto || 0);
      const tamanho = String(item.tamanho || '').trim().toUpperCase();
      const quantidade = Math.max(0, Math.trunc(Number(item.quantidade || 0)));
      const defeitos = Math.max(0, Math.trunc(Number(item.defeitos || 0)));

      if (!idProduto || !this.tamanhosBaseProduto(idProduto).includes(tamanho) || quantidade + defeitos <= 0) continue;

      const existenteIndex = this.itens.controls.findIndex((control) =>
        Number(control.value.id_produto || 0) === idProduto && control.value.tamanho === tamanho
      );

      if (existenteIndex >= 0) {
        this.itens.at(existenteIndex).patchValue({ quantidade, defeitos });
      } else {
        this.itens.push(this.itemGroup(idProduto, tamanho, quantidade, defeitos));
      }
      aplicados += 1;
    }

    if (aplicados) {
      const primeiroProduto = this.itens.at(0)?.value.id_produto || '';
      this.form.controls.id_produto.setValue(String(primeiroProduto));
      const produto = this.produtos.find((item) => item.cod_produto === Number(primeiroProduto || 0));
      if (produto && !this.form.controls.referencia.value) this.form.controls.referencia.setValue(produto.codigo_produto);
      if (produto && !this.form.controls.descricao.value) this.form.controls.descricao.setValue(produto.descricao);
      this.itens.markAsDirty();
      this.itens.updateValueAndValidity();
    }

    return aplicados;
  }

  private aplicarRestricoesDaNota(nota: NotaTransferencia) {
    this.tamanhosConferidosPorProduto = this.criarMapaTamanhosConferidos(nota);
    this.produtosDaNota = [...this.tamanhosConferidosPorProduto.keys()];
  }

  private aplicarRascunhoExistente(anexos: ContagemAnexo[]) {
    this.rascunhoExistente = anexos.find((anexo) => anexo.tipo === 'RASCUNHO');
    this.rascunhoFoto = undefined;
    this.removerRascunhoExistente = false;

    if (!this.rascunhoExistente) {
      this.rascunhoArquivoNome = '';
      this.definirPreviewRascunho('');
      return;
    }

    this.rascunhoArquivoNome = this.rascunhoExistente.nome_original || 'rascunho-contagem.jpg';
    this.api.anexoContagem(this.id, this.rascunhoExistente.id_anexo).subscribe({
      next: (blob) => {
        if (!this.rascunhoExistente || this.rascunhoFoto) return;
        const url = URL.createObjectURL(blob);
        this.definirPreviewRascunho(url, true);
      },
      error: () => {
        this.rascunhoArquivoNome = '';
        this.definirPreviewRascunho('');
      }
    });
  }

  private criarMapaTamanhosConferidos(nota: NotaTransferencia) {
    const mapa = new Map<number, string[]>();
    (nota.saldos || [])
      .filter((item) => Number(item.id_produto || 0) > 0 && !!item.tamanho && Number(item.total_conferido || 0) > 0)
      .forEach((item) => {
        const idProduto = Number(item.id_produto || 0);
        const tamanhos = mapa.get(idProduto) || [];
        if (item.tamanho && !tamanhos.includes(item.tamanho)) tamanhos.push(item.tamanho);
        mapa.set(idProduto, tamanhos);
      });

    mapa.forEach((tamanhos, idProduto) => {
      mapa.set(idProduto, tamanhos.sort((a, b) => TAMANHOS.indexOf(a) - TAMANHOS.indexOf(b)));
    });
    return mapa;
  }

  private itemGroup(idProduto: number, tamanho: string, quantidade: number, defeitos = 0) {
    return this.fb.nonNullable.group({
      id_produto: [idProduto],
      tamanho: [tamanho],
      quantidade: [quantidade, [Validators.required, Validators.min(0)]],
      defeitos: [defeitos, [Validators.required, Validators.min(0)]]
    });
  }

  private async prepararImagemRascunho(file: File): Promise<ContagemFotoUpload> {
    const extensaoImagem = /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!file.type.startsWith('image/') && !extensaoImagem) {
      throw new Error('Selecione uma imagem valida para o rascunho');
    }
    if (file.size > 15 * 1024 * 1024) {
      throw new Error('A imagem original deve ter ate 15 MB');
    }

    const image = await this.carregarImagem(file);
    const maxDimension = 1600;
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Nao foi possivel preparar a imagem');

    context.drawImage(image, 0, 0, width, height);

    let blob: Blob | null = null;
    for (const quality of [0.82, 0.72, 0.62]) {
      blob = await this.canvasToBlob(canvas, 'image/jpeg', quality);
      if (blob && blob.size <= 5 * 1024 * 1024) break;
    }
    if (!blob || blob.size > 5 * 1024 * 1024) {
      throw new Error('Nao foi possivel compactar a foto para ate 5 MB');
    }

    return {
      nome: this.nomeRascunhoJpg(file.name),
      tipo: 'image/jpeg',
      dados: await this.blobToDataUrl(blob)
    };
  }

  private carregarImagem(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Nao foi possivel ler essa imagem'));
      };
      image.src = url;
    });
  }

  private canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Nao foi possivel ler a foto preparada'));
      reader.readAsDataURL(blob);
    });
  }

  private nomeRascunhoJpg(nome: string) {
    const base = String(nome || 'rascunho-contagem').replace(/\.[^.]+$/, '').trim() || 'rascunho-contagem';
    return `${base.slice(0, 120)}.jpg`;
  }

  private definirPreviewRascunho(url: string, objectUrl = false) {
    this.liberarPreviewTemporario();
    this.rascunhoPreviewUrl = url;
    this.rascunhoObjectUrl = objectUrl ? url : '';
  }

  private liberarPreviewTemporario() {
    if (!this.rascunhoObjectUrl) return;
    URL.revokeObjectURL(this.rascunhoObjectUrl);
    this.rascunhoObjectUrl = '';
  }
}
